<div class="header-box">
  <h1>{{ mainTitle() }}</h1>
  <h2>@if (idLabel()) {<span class="id-label">{{ idLabel() }}</span>}{{ id() }}</h2>
</div>

<div class="description-box">
  <p [innerHTML]="description()"></p>
</div>

<section class="accordion-container">
  @if (subTitle()) {
    <h2 class="section-heading">{{ subTitle() | transloco }}</h2>
  }

  @for (block of blockConfigs(); track block.title) {
    <div class="accordion-section" [class.open]="isSectionOpen(block.title)">
      <div
        class="accordion-header"
        role="button"
        tabindex="0"
        [attr.aria-expanded]="isSectionOpen(block.title)"
        (click)="toggleSection(block.title)"
        (keydown.enter)="toggleSection(block.title)"
        (keydown.space)="$event.preventDefault(); toggleSection(block.title)"
      >
        <img
          class="accordion-icon"
          src="./assets/images/sheet-with-{{ block.icon }}.png"
          alt=""
          width="40"
          height="45"
        />
        <span class="accordion-title">{{ block.title | transloco }}</span>
        <mat-icon class="accordion-chevron">expand_more</mat-icon>
      </div>
      <div class="accordion-body">
        @if (block.description) {
          <p class="accordion-description">{{ block.description | transloco }}</p>
        }
        @if (block.policyCards?.length) {
          <div class="policy-cards-container">
            @for (card of block.policyCards; track $index) {
              <div class="policy-card" [class]="'policy-card severity-' + card.severity">
                <div class="policy-card-header">
                  <div class="policy-icon-box">
                    <mat-icon fontSet="material-icons-outlined">{{ card.icon }}</mat-icon>
                  </div>
                  <div class="policy-card-info">
                    <div class="policy-card-title">{{ card.title }}</div>
                    <div class="policy-card-explanation">{{ card.explanation }}</div>
                    <mat-chip-set class="policy-badge-chip-set">
                      <mat-chip class="entity-card-chip">
                        <mat-icon class="policy-badge-icon" fontSet="material-icons-outlined">{{ card.badgeIcon }}</mat-icon>
                        {{ card.typeBadge }}
                      </mat-chip>
                    </mat-chip-set>
                  </div>
                </div>
                <div class="policy-constraints-area">
                  <div class="constraint-divider"></div>
                  @if (card.noConstraints) {
                    <div class="no-constraints-badge">
                      <mat-icon fontSet="material-icons-outlined">verified_user</mat-icon>
                      {{ 'policy.card.noRestrictions' | transloco }}
                    </div>
                  } @else {
                    <div class="constraint-rows">
                      @for (row of card.constraintRows; track $index; let isLast = $last) {
                        <div class="constraint-row">
                          <span class="constraint-label">{{ row.label }}</span>
                          <span class="constraint-value" [class.mono]="row.mono">{{ row.value }}</span>
                        </div>
                        @if (!isLast) {
                          <div class="and-connector">
                            <span class="and-line"></span>
                            <span class="and-badge">{{ 'policy.card.and' | transloco }}</span>
                            <span class="and-line"></span>
                          </div>
                        }
                      }
                    </div>
                  }
                </div>
              </div>
            }
          </div>
        } @else {
          <div class="accordion-content">
            @for (item of block.blockItems; track item.title) {
              <div class="accordion-kv" [class.accordion-kv-chips]="!!item.chips">
                <span class="accordion-kv-label">{{ item.title | transloco }}</span>
                @if (item.badge) {
                  <span class="status-badge status-badge--{{ item.badge.variant }}">
                    <span class="status-badge-dot"></span>
                    {{ item.badge.label }}
                  </span>
                } @else if (item.chips) {
                  <mat-chip-set class="accordion-chip-set" aria-label="{{ item.title | transloco }}">
                    @for (chip of item.chips; track chip) {
                      <mat-chip class="entity-card-chip">{{ chip }}</mat-chip>
                    }
                  </mat-chip-set>
                } @else if (item.copyable && item.content) {
                  <span class="accordion-kv-copyable">
                    <span class="accordion-kv-url">{{ item.content }}</span>
                    <button
                      mat-icon-button
                      class="copy-button"
                      [matTooltip]="copiedKey === item.title ? ('common.copied' | transloco) : ('common.copy' | transloco)"
                      (click)="copyToClipboard(item.content!, item.title)"
                    >
                      <mat-icon>{{ copiedKey === item.title ? 'check' : 'content_copy' }}</mat-icon>
                    </button>
                  </span>
                } @else {
                  <span class="accordion-kv-value">{{ item.content | transloco | emptyValue }}</span>
                }
              </div>
            }

            @if (block.actionButton) {
              <div class="block-action">
                <button mat-stroked-button (click)="actionButtonClicked.emit(block.title); $event.stopPropagation()">
                  @if (block.actionButton.icon) {
                    <mat-icon fontSet="material-icons-outlined">{{ block.actionButton.icon }}</mat-icon>
                  }
                  {{ block.actionButton.label | transloco }}
                </button>
              </div>
            }

            @if (block.dataSource) {
              <div class="datasource-panel">
                <div class="datasource-header">
                  <mat-icon class="datasource-header-icon" fontSet="material-icons-outlined">storage</mat-icon>
                  <span class="datasource-header-label">{{ 'asset.dataSource.header' | transloco }}</span>
                </div>

                <div class="datasource-body">
                  <div class="datasource-primary">
                    <div class="datasource-type">
                      <span class="datasource-type-label">{{ 'asset.dataAddress.type.label' | transloco }}</span>
                      <span class="datasource-type-value">
                        <mat-icon class="datasource-type-icon" fontSet="material-icons-outlined">dns</mat-icon>
                        {{ block.dataSource.type }}
                      </span>
                    </div>
                    <div class="datasource-url">
                      <span class="datasource-url-label">{{ 'asset.dataAddress.baseUrl.label' | transloco }}</span>
                      <span class="datasource-url-value">
                        <span class="datasource-url-text">{{ block.dataSource.url }}</span>
                        <button
                          mat-icon-button
                          class="copy-button"
                          [matTooltip]="copiedKey === 'dataSource.url' ? ('common.copied' | transloco) : ('common.copy' | transloco)"
                          (click)="copyToClipboard(block.dataSource.url, 'dataSource.url')"
                        >
                          <mat-icon>{{ copiedKey === 'dataSource.url' ? 'check' : 'content_copy' }}</mat-icon>
                        </button>
                      </span>
                    </div>
                  </div>

                  <div class="datasource-capabilities">
                    @for (cap of block.dataSource.capabilities; track cap.label) {
                      <div class="datasource-cap" [class.datasource-cap-enabled]="cap.enabled" [class.datasource-cap-disabled]="!cap.enabled">
                        <mat-icon class="datasource-cap-icon" fontSet="material-icons-outlined">
                          {{ cap.enabled ? 'check_circle' : 'cancel' }}
                        </mat-icon>
                        <span class="datasource-cap-label">{{ cap.label | transloco }}</span>
                      </div>
                    }
                  </div>

                  <div class="datasource-auth">
                    <div class="datasource-auth-badge">
                      <mat-icon class="datasource-auth-icon" fontSet="material-icons-outlined">{{ block.dataSource.auth.icon }}</mat-icon>
                      <div class="datasource-auth-info">
                        <span class="datasource-auth-label">{{ 'asset.dataAddress.auth.label' | transloco }}</span>
                        <span class="datasource-auth-value">{{ block.dataSource.auth.label | transloco }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            }
          </div>
        }
      </div>
    </div>
  }
</section>
